import { evaluate, type Evaluated, parentsOf } from '../../construction';
import { angle, type Vec2, vec } from '../../math';
import { type EntityId, kindOf, type SceneDocument, topologicalOrder } from '../../model';
import { type Vec3 } from '../../solid';
import { parseScript, type ScriptError } from '../parse';

/**
 * The checks a golden case is written in.
 *
 * ## Why properties and not expected output
 *
 * The obvious golden set stores a problem and the script it should produce, and compares. It
 * is the wrong instrument here, for three reasons that all bite immediately:
 *
 * 1. **Many scripts draw the same figure.** Naming the apex `S` or `dinh`, placing the base
 *    before the axis, using `midpoint A B` or `M = intersect ...` — all correct, all different
 *    text. A diff punishes correct variation, so the set fills up with false failures and is
 *    then ignored.
 * 2. **It freezes the language.** Every statement added — `oval`, `hide`, `style` — rewrites
 *    every stored script. A corpus that has to be regenerated whenever the product improves is
 *    a corpus nobody maintains.
 * 3. **It measures the wrong thing.** What matters is whether the *figure* is right, and a
 *    script is one of many ways to say so.
 *
 * So a case states what has to be **true of the drawing**: this object exists, M really is the
 * midpoint of AB, K really lies on SM, nothing evaluated to nothing. Those survive a rewritten
 * script, a new statement, and a different author — human or model.
 *
 * ## What this deliberately cannot check
 *
 * Whether the figure *reads well*. Whether the labels are legible, whether the base looks like
 * a base, whether an angle is open enough to hold its number. Every one of those was a real
 * defect in this project and none is mechanically decidable — they need eyes.
 *
 * Pretending otherwise is the failure mode to avoid: a suite that scores nine out of ten while
 * the figure is unreadable is worse than no suite, because it is trusted. The mechanical checks
 * catch what they can catch *exactly*, and the rest stays a human's job.
 */

export interface Figure {
  readonly document: SceneDocument;
  readonly values: ReadonlyMap<EntityId, Evaluated | null>;
  readonly errors: readonly ScriptError[];
  /** Script name -> entity id, so a check can talk about `M` rather than about `e7`. */
  readonly names: ReadonlyMap<string, EntityId>;
}

/** A single assertion about a figure. Returns `null` when satisfied, or what went wrong. */
export type Check = (figure: Figure) => string | null;

/**
 * Runs a script and evaluates it.
 *
 * The graph is walked here rather than through `engine`: `domain` may not import it, and a
 * test harness is not exempt from the boundary it runs under.
 */
export function drawFrom(source: string): Figure {
  const result = parseScript(source);

  const values = new Map<EntityId, Evaluated | null>();
  for (const id of topologicalOrder(result.document) ?? []) {
    const entity = result.document.entities.get(id);
    if (entity === undefined) continue;
    const parents = parentsOf(entity.source).map((parent) => values.get(parent) ?? null);
    values.set(id, evaluate(entity.source, parents));
  }

  return {
    document: result.document,
    values,
    errors: result.errors,
    /*
     * Taken from the parser rather than reconstructed.
     *
     * The first version re-read the script here and paired each assignment with the id at the
     * matching position, on the reasoning that ids are allocated in statement order. It was
     * wrong on the very first case and wrong *silently* — every named lookup missed, so the
     * checks reported "need points O, A, B" for a figure that had all three. Inferring
     * something the parser already knows is guesswork whatever the reasoning behind it.
     */
    names: result.names,
  };
}

/* ------------------------------------------------------------------ the checks */

/** Every line was understood. The first thing a teacher notices, and the easiest to assert. */
export const parses = (): Check => (figure) =>
  figure.errors.length === 0
    ? null
    : figure.errors.map((error) => `line ${String(error.line)}: ${error.message}`).join('; ');

/**
 * Nothing evaluated to nothing.
 *
 * The failure a teacher cannot diagnose: the script is accepted, the object is in the list, and
 * it is simply absent from the picture. Far worse than a parse error, which at least says so.
 */
export const nothingMissing = (): Check => (figure) => {
  const dead = [...figure.document.entities.values()].filter(
    (entity) => figure.values.get(entity.id) == null
  );
  return dead.length === 0
    ? null
    : `${String(dead.length)} object(s) drew nothing: ${dead.map((entity) => entity.source.op).join(', ')}`;
};

/**
 * The figure contains a solid of this kind.
 *
 * Unlike {@link has} this asks about no *name*, and that is the point. A solid statement names
 * its corners, not itself, so there is nothing to look up — and the failure this catches is a
 * figure that draws everything around a shape and not the shape: asked for the sphere
 * circumscribing a pyramid, a model drew the pyramid, labelled the centre `O`, and stopped.
 * Every point the problem mentions was present and correct, so every other check passed.
 */
export const hasSolid =
  (kind: 'prism' | 'pyramid' | 'cone' | 'cylinder' | 'sphere'): Check =>
  (figure) => {
    const found = [...figure.document.entities.values()].some(
      (entity) => entity.source.op === 'solid-figure' && entity.source.solid === kind
    );
    return found ? null : `the figure has no ${kind}`;
  };

/**
 * The name the script gave the vertex in the *other* ring, above or below a named one.
 *
 * For checking a generated figure, where the one thing genuinely free is what to call `A'`. The
 * language has no apostrophe in a name, so a script must transliterate, and three spellings
 * turned up in two runs of the eval — `A1`, `A2`, `Ap`. A check that guesses one of them reports
 * a figure that is right in every respect as a failure, which is worse than not checking.
 *
 * Nothing has to be guessed, because the pairing is a property of the language: a prism writes
 * `A B C . X Y Z` and the *k*th name on one side is the corner above the *k*th on the other, so
 * "the vertex above A" is decided by position and not by spelling. This walks that: name to
 * vertex index, across the ring, back to whatever name the script used.
 *
 * Returns `null` for a name that is not a corner of a prism — a pyramid apex has no twin, and
 * neither does an ordinary point.
 */
export function twinOf(figure: Figure, name: string): string | null {
  const id = figure.names.get(name);
  const entity = id === undefined ? undefined : figure.document.entities.get(id);
  if (entity?.source.op !== 'solid-vertex') return null;

  const { solid, index } = entity.source;
  const figureEntity = figure.document.entities.get(solid);
  if (figureEntity?.source.op !== 'solid-figure') return null;

  const ring = figureEntity.source.corners;
  if (ring <= 0) return null;
  const across = index < ring ? index + ring : index - ring;

  for (const [candidate, other] of figure.names) {
    const found = figure.document.entities.get(other);
    if (
      found?.source.op === 'solid-vertex' &&
      found.source.solid === solid &&
      found.source.index === across
    ) {
      return candidate;
    }
  }
  return null;
}

/**
 * A check about a corner and the one across the ring from it, whatever the script called them.
 *
 * `across('A', (a2) => spaceAngle(a2, 'H', 'A', 90))` asks the question the problem asks —
 * "A' stands over H" — without asserting anything about spelling.
 */
export const across =
  (name: string, make: (twin: string) => Check): Check =>
  (figure) => {
    const twin = twinOf(figure, name);
    return twin === null
      ? `\`${name}\` is not a corner of a prism, so nothing stands across the ring from it`
      : make(twin)(figure);
  };

/**
 * The three checks a two-view drawing turns on.
 *
 * An đồ thức says two things and both are about *where*: the vertical projection belongs above
 * the ground line and the horizontal one below it, and the pair must share a coordinate along
 * the axis. Flip the first and every line of the diagram is still drawable while every statement
 * it makes is false; break the second and the two are projections of different points.
 *
 * Written against the axis at `y = 0`, which is where `ground` puts it. That is a convention
 * rather than a law, and it is the parser's convention — so a case asserting it is what stops
 * the two drifting apart.
 */
export const above =
  (name: string): Check =>
  (figure) => {
    const at = pointAt(figure, name);
    if (at === null) return `no point named \`${name}\``;
    return at.y > 0 ? null : `${name} is at y = ${at.y.toFixed(0)}, not above the ground line`;
  };

export const below =
  (name: string): Check =>
  (figure) => {
    const at = pointAt(figure, name);
    if (at === null) return `no point named \`${name}\``;
    return at.y < 0 ? null : `${name} is at y = ${at.y.toFixed(0)}, not below the ground line`;
  };

/**
 * A point sits on the ground line.
 *
 * The signature of a trace, and the one thing about it that can be checked without redoing the
 * arithmetic: a vết đứng has độ xa zero, so its *horizontal* projection is on the axis, and a
 * vết bằng the other way round. Reverse the two and the diagram still draws — it simply says
 * the opposite of what the problem says.
 */
export const onAxis =
  (name: string, tolerance = 1e-6): Check =>
  (figure) => {
    const at = pointAt(figure, name);
    if (at === null) return `no point named \`${name}\``;
    return Math.abs(at.y) <= tolerance
      ? null
      : `${name} is at y = ${at.y.toFixed(1)}, not on the ground line`;
  };

/** Two projections of one point stand on the same correspondence line. */
export const inLine =
  (first: string, second: string, tolerance = 1e-6): Check =>
  (figure) => {
    const a = pointAt(figure, first);
    const b = pointAt(figure, second);
    if (a === null || b === null) return `need points ${first} and ${second}`;
    return Math.abs(a.x - b.x) <= tolerance
      ? null
      : `${first} is at x = ${a.x.toFixed(1)} and ${second} at ${b.x.toFixed(1)}, so they are ` +
          'not two projections of the same point';
  };

/** A named object exists, and is of the kind the figure needs it to be. */
export const has =
  (name: string, kind?: string): Check =>
  (figure) => {
    const id = figure.names.get(name);
    const entity = id === undefined ? undefined : figure.document.entities.get(id);
    if (entity === undefined) return `no object named \`${name}\``;
    if (kind !== undefined && kindOf(entity.source) !== kind) {
      return `\`${name}\` is a ${kindOf(entity.source)}, not a ${kind}`;
    }
    return null;
  };

/** Objects that must be named on the drawing, because the problem refers to them by name. */
export const labelled =
  (...names: readonly string[]): Check =>
  (figure) => {
    const missing = names.filter((name) => {
      const id = figure.names.get(name);
      const entity = id === undefined ? undefined : figure.document.entities.get(id);
      return entity?.label === undefined;
    });
    return missing.length === 0 ? null : `unlabelled: ${missing.join(', ')}`;
  };

const pointAt = (figure: Figure, name: string) => {
  const id = figure.names.get(name);
  const value = id === undefined ? null : (figure.values.get(id) ?? null);
  return value?.kind === 'point' ? value.point : null;
};

/** `m` really is the midpoint of `a` and `b`, rather than a point placed near it. */
export const isMidpoint =
  (m: string, a: string, b: string): Check =>
  (figure) => {
    const [pm, pa, pb] = [pointAt(figure, m), pointAt(figure, a), pointAt(figure, b)];
    if (pm === null || pa === null || pb === null) return `need points ${m}, ${a}, ${b}`;

    const expected = vec.scale(vec.add(pa, pb), 0.5);
    return vec.distance(pm, expected) < 1e-6 ? null : `${m} is not the midpoint of ${a}${b}`;
  };

/** `p` lies on the segment from `a` to `b` — the check that a foot really is a foot. */
export const onSegment =
  (p: string, a: string, b: string): Check =>
  (figure) => {
    const [pp, pa, pb] = [pointAt(figure, p), pointAt(figure, a), pointAt(figure, b)];
    if (pp === null || pa === null || pb === null) return `need points ${p}, ${a}, ${b}`;

    const total = vec.distance(pa, pb);
    const detour = vec.distance(pa, pp) + vec.distance(pp, pb);
    return Math.abs(detour - total) < 1e-6 * Math.max(1, total)
      ? null
      : `${p} does not lie on ${a}${b}`;
  };

/** Two lengths agree — `OA = OB` for two points on the same circle, say. */
export const sameLength =
  (o: string, a: string, b: string, tolerance = 1): Check =>
  (figure) => {
    const [po, pa, pb] = [pointAt(figure, o), pointAt(figure, a), pointAt(figure, b)];
    if (po === null || pa === null || pb === null) return `need points ${o}, ${a}, ${b}`;

    const first = vec.distance(po, pa);
    const second = vec.distance(po, pb);
    return Math.abs(first - second) <= tolerance
      ? null
      : `${o}${a} is ${first.toFixed(1)} but ${o}${b} is ${second.toFixed(1)}`;
  };

/**
 * No two named points sit on top of each other.
 *
 * A figure where two vertices coincide is degenerate, and it is the shape a generated script
 * takes when it loses track of its own coordinates — every line still draws, and the picture is
 * nonsense.
 */
export const noCoincidentPoints =
  (minimum = 4): Check =>
  (figure) => {
    const points = [...figure.names].flatMap(([name, id]) => {
      const value = figure.values.get(id) ?? null;
      return value?.kind === 'point' ? [{ name, at: value.point }] : [];
    });

    for (let i = 0; i < points.length; i += 1) {
      for (let j = i + 1; j < points.length; j += 1) {
        const first = points[i];
        const second = points[j];
        if (first === undefined || second === undefined) continue;
        if (vec.distance(first.at, second.at) < minimum) {
          return `${first.name} and ${second.name} are on top of each other`;
        }
      }
    }
    return null;
  };

/**
 * The whole figure fits in a sensible box.
 *
 * Catches the runaway: a coordinate off by a factor of a thousand still draws, and the export
 * frames on it, so every other object collapses into a dot in the corner.
 */
export const withinBox =
  (limit = 2000): Check =>
  (figure) => {
    for (const [name, id] of figure.names) {
      const at = pointAt({ ...figure }, name);
      void id;
      if (at === null) continue;
      if (Math.abs(at.x) > limit || Math.abs(at.y) > limit) {
        return `${name} is at (${at.x.toFixed(0)}, ${at.y.toFixed(0)}), outside the drawing`;
      }
    }
    return null;
  };

/**
 * `p` lies on the ellipse named by `ellipse`.
 *
 * ## Why this exists, and what it taught
 *
 * The cone case first checked that A and B were equidistant from the centre — they lie on the
 * base circle, so of course they are. It failed: 170 against 102.
 *
 * The check was wrong, not the figure. A projected drawing holds *projected* coordinates, and a
 * distance is not preserved by projection — the base circle arrives as an ellipse, and two
 * points on it are equidistant in space and nothing like it on the page.
 *
 * That is the rule this whole file turns on: **a check must be a property that survives the
 * representation the figure is stored in.** Affine facts do survive — a midpoint stays a
 * midpoint, a point on a segment stays on it, a point on a conic stays on the conic. Metric
 * facts do not. Writing a metric check for a solid figure produces a red suite and a correct
 * drawing, which is the way a golden set loses its readers.
 */
export const onEllipse =
  (p: string, ellipse: string, tolerance = 0.04): Check =>
  (figure) => {
    const at = pointAt(figure, p);
    const id = figure.names.get(ellipse);
    const value = id === undefined ? null : (figure.values.get(id) ?? null);

    if (at === null) return `need point ${p}`;
    if (value?.kind !== 'ellipse') return `\`${ellipse}\` is not an ellipse`;

    const { center, rx, ry, rotation } = value.ellipse;
    if (rx === 0 || ry === 0) return `\`${ellipse}\` has no size`;

    /*
     * Turned back onto its own axes before measuring.
     *
     * This used to divide by `rx` and `ry` directly, which is only right for an ellipse lying
     * square to the page — true of every `oval`, and false of the one a projected circle
     * becomes. The first rotated ellipse this met reported two points off the rim by 24% and
     * 8% when both were exactly on it.
     */
    const offset = vec.rotate(vec.subtract(at, center), -rotation);
    const radial = Math.hypot(offset.x / rx, offset.y / ry);

    return Math.abs(radial - 1) <= tolerance
      ? null
      : `${p} is not on ${ellipse} (radially ${radial.toFixed(3)}, wanted 1)`;
  };

/**
 * Two spans are equal: |ab| = |cd|.
 *
 * For an isometry — a reflection, a rotation, a translation — where what has to hold is that
 * the image is the same size as the original, side for side.
 */
export const sameSpan =
  (a: string, b: string, c: string, d: string, tolerance = 1e-6): Check =>
  (figure) => {
    const points = [pointAt(figure, a), pointAt(figure, b), pointAt(figure, c), pointAt(figure, d)];
    if (points.some((point) => point === null)) return `need points ${a}, ${b}, ${c}, ${d}`;

    const [pa, pb, pc, pd] = points as [
      ReturnType<typeof pointAt> & object,
      ReturnType<typeof pointAt> & object,
      ReturnType<typeof pointAt> & object,
      ReturnType<typeof pointAt> & object,
    ];

    const first = vec.distance(pa, pb);
    const second = vec.distance(pc, pd);

    return Math.abs(first - second) <= tolerance * Math.max(1, first)
      ? null
      : `${a}${b} is ${first.toFixed(2)} but ${c}${d} is ${second.toFixed(2)}`;
  };

/**
 * No two lines leave the same corner at nearly the same angle.
 *
 * ## What this catches that nothing else does
 *
 * A figure can be geometrically perfect and still unreadable, and the commonest way is a
 * *degenerate view*: two segments meeting at a vertex that the projection lays almost on top of
 * one another. The reader sees one line where the construction has two, and if one of them is
 * dashed and the other solid the picture actively lies about which is which.
 *
 * Descriptive geometry has a name for the remedy — putting the solid in **general position**,
 * so that no edge projects to a point, no face to a line, and no two edges to the same line.
 * This is the last of those three, measured.
 *
 * ## Why it is a threshold and not a proof
 *
 * There is no angle at which two lines *become* distinguishable; it is a matter of degree, of
 * stroke width and of how far the lines run. Six degrees is where this corpus sat once the
 * projection was fixed, and it is tight rather than comfortable — the value is not that the
 * number is right but that a change making some figure sharply worse cannot pass unnoticed.
 *
 * Measured on a real corpus, the depth angle moved this from 2.8° to 6.1°; the figure that
 * drove it was a pyramid where `AS` and `AJ` left the same corner 2.8° apart, with `AJ` dashed
 * and hugging a solid edge for its whole length.
 */
export const noAmbiguousCorner =
  (minimumDegrees = 5): Check =>
  (figure) => {
    const segments: { a: Vec2; b: Vec2 }[] = [];
    for (const entity of figure.document.entities.values()) {
      if (!entity.visible) continue;
      const value = figure.values.get(entity.id);
      if (value?.kind === 'segment') segments.push({ a: value.segment.a, b: value.segment.b });
    }

    const meets = (p: Vec2, q: Vec2): boolean => vec.distance(p, q) < 1e-6;
    let worst: { degrees: number; at: Vec2 } | null = null;

    for (let i = 0; i < segments.length; i += 1) {
      for (let j = i + 1; j < segments.length; j += 1) {
        const first = segments[i];
        const second = segments[j];
        if (first === undefined || second === undefined) continue;

        for (const [from, to] of [
          [first.a, first.b],
          [first.b, first.a],
        ] as const) {
          for (const [origin, towards] of [
            [second.a, second.b],
            [second.b, second.a],
          ] as const) {
            if (!meets(from, origin)) continue;
            const between = Math.abs(
              (angle.normalizeSigned(
                vec.angle(vec.subtract(to, from)) - vec.angle(vec.subtract(towards, origin))
              ) *
                180) /
                Math.PI
            );
            if (worst === null || between < worst.degrees) worst = { degrees: between, at: from };
          }
        }
      }
    }

    if (worst === null || worst.degrees >= minimumDegrees) return null;
    return `two lines leave (${worst.at.x.toFixed(0)}, ${worst.at.y.toFixed(0)}) only ${worst.degrees.toFixed(1)}° apart, which draws as one line`;
  };

/**
 * Two spans stand in a given ratio: |ab| = k·|cd|.
 *
 * ## What it is for
 *
 * Stating that the figure is the shape the *problem* says it is. "Đáy ABCD là hình vuông" is a
 * claim about the solid, and a figure that draws that base as a rectangle contradicts the
 * question it illustrates — which is a worse failure than any of the legibility ones, because
 * the drawing is now simply false.
 *
 * Squareness is not affine, so it cannot be read off the page in general. It can be read off
 * *this* page: the figure projection leaves the front face true and halves the depth, so a
 * square base is exactly the one whose front edge measures twice its depth edge. The ratio is
 * therefore a fact about the drawing, and this check is deliberately tied to that projection —
 * if the foreshortening ever changes, these are the assertions that should be revisited.
 *
 * The same instrument states a cube: its vertical edge is unforeshortened and its front edge is
 * true, so the two come out equal, and `sameSpan` already says that.
 */
export const spanRatio =
  (a: string, b: string, c: string, d: string, ratio: number, tolerance = 1e-6): Check =>
  (figure) => {
    const points = [pointAt(figure, a), pointAt(figure, b), pointAt(figure, c), pointAt(figure, d)];
    if (points.some((point) => point === null)) return `need points ${a}, ${b}, ${c}, ${d}`;

    const [pa, pb, pc, pd] = points as [Vec2, Vec2, Vec2, Vec2];
    const first = vec.distance(pa, pb);
    const second = vec.distance(pc, pd);
    if (second === 0) return `${c}${d} has no length`;

    const found = first / second;
    return Math.abs(found - ratio) <= tolerance * Math.max(1, ratio)
      ? null
      : `${a}${b} is ${found.toFixed(3)} times ${c}${d}, wanted ${String(ratio)}`;
  };

/**
 * The angle at a corner, measured **in space**.
 *
 * The one property of a solid figure that the page cannot be asked about. A projection does not
 * preserve an angle, so a 120° corner comes out at some other number on the drawing and every
 * check here that measures the page would be measuring the projection instead.
 *
 * It exists because a figure was wrong in exactly this way and nothing caught it. "Đáy ABC là
 * tam giác cân với AB = AC = a, góc BAC = 120°" was drawn as `size 450 220 150`, with a comment
 * explaining that a triangle inscribed in a 3:1 ellipse has a 120° apex — true, and a scalene
 * triangle rather than the isosceles one the problem describes. The apex was right and the two
 * sides were not, and every check passed because none of them could see into space.
 *
 * Returns "not on a solid" rather than passing when a point has no position in space. A check
 * that quietly succeeds on the figures it cannot inspect is worse than one that is absent.
 */
export const spaceAngle =
  (a: string, vertex: string, c: string, degrees: number, tolerance = 0.01): Check =>
  (figure) => {
    const spaceOf = (name: string): Vec3 | null => {
      const id = figure.names.get(name);
      const value = id === undefined ? null : figure.values.get(id);
      return value?.kind === 'point' ? (value.in3d ?? null) : null;
    };

    const points = [spaceOf(a), spaceOf(vertex), spaceOf(c)];
    if (points.some((point) => point === null)) {
      return `${a}, ${vertex} and ${c} are not all corners of a solid, so they have no angle in space`;
    }
    const [pa, pv, pc] = points as [Vec3, Vec3, Vec3];

    const u = { x: pa.x - pv.x, y: pa.y - pv.y, z: pa.z - pv.z };
    const v = { x: pc.x - pv.x, y: pc.y - pv.y, z: pc.z - pv.z };
    const lengths = Math.hypot(u.x, u.y, u.z) * Math.hypot(v.x, v.y, v.z);
    if (lengths === 0) return `${a}${vertex}${c} has no angle: two of its points coincide`;

    const cosine = (u.x * v.x + u.y * v.y + u.z * v.z) / lengths;
    const found = (Math.acos(Math.max(-1, Math.min(1, cosine))) * 180) / Math.PI;

    return Math.abs(found - degrees) <= tolerance
      ? null
      : `angle ${a}${vertex}${c} is ${found.toFixed(2)}° in space but the problem says ${String(degrees)}°`;
  };

/**
 * Two sides of the same length, measured **in space**.
 *
 * `sameSpan` compares them on the page, which is right for a prism's lateral edges — a
 * translation survives projection — and wrong for anything else. AB and AC of an isosceles
 * triangle run in different directions, so the projection is free to draw them at different
 * lengths, and it does.
 */
export const sameSpaceSpan =
  (a: string, b: string, c: string, d: string, tolerance = 1e-6): Check =>
  (figure) => {
    const spaceOf = (name: string): Vec3 | null => {
      const id = figure.names.get(name);
      const value = id === undefined ? null : figure.values.get(id);
      return value?.kind === 'point' ? (value.in3d ?? null) : null;
    };

    const points = [spaceOf(a), spaceOf(b), spaceOf(c), spaceOf(d)];
    if (points.some((point) => point === null)) {
      return `${a}, ${b}, ${c} and ${d} are not all corners of a solid`;
    }
    const [pa, pb, pc, pd] = points as [Vec3, Vec3, Vec3, Vec3];

    const between = (p: Vec3, q: Vec3): number => Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z);
    const first = between(pa, pb);
    const second = between(pc, pd);

    return Math.abs(first - second) <= tolerance
      ? null
      : `${a}${b} is ${first.toFixed(2)} in space but ${c}${d} is ${second.toFixed(2)}`;
  };

/**
 * One side a stated multiple of another, measured **in space**.
 *
 * `sameSpaceSpan` with a number, and it exists because equality is not what a problem usually
 * states. "SA = a√2" relates the height to the side of the base, and getting it wrong is a
 * separate mistake from getting the *angle* wrong — a figure can have the apex over the right
 * corner and still stand it at the wrong height.
 *
 * It found its first defect immediately. Asked for "SA ⊥ (ABC), AB = a, SA = 2a", a model wrote
 * a pyramid with no `over` clause: the apex went above the centre of the base, which made the
 * angle at A 77.08° instead of 90° *and* stretched SA to 2.236 a. One cause, two wrong
 * statements in the drawing, and only this check can see the second.
 */
export const spaceSpanRatio =
  (a: string, b: string, c: string, d: string, ratio: number, tolerance = 1e-3): Check =>
  (figure) => {
    const spaceOf = (name: string): Vec3 | null => {
      const id = figure.names.get(name);
      const value = id === undefined ? null : figure.values.get(id);
      return value?.kind === 'point' ? (value.in3d ?? null) : null;
    };

    const points = [spaceOf(a), spaceOf(b), spaceOf(c), spaceOf(d)];
    if (points.some((point) => point === null)) {
      return `${a}, ${b}, ${c} and ${d} are not all corners of a solid`;
    }
    const [pa, pb, pc, pd] = points as [Vec3, Vec3, Vec3, Vec3];

    const between = (p: Vec3, q: Vec3): number => Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z);
    const first = between(pa, pb);
    const second = between(pc, pd);
    if (second === 0) return `${c}${d} has no length in space`;

    const found = first / second;
    return Math.abs(found - ratio) <= tolerance * Math.max(1, ratio)
      ? null
      : `${a}${b} is ${found.toFixed(3)} times ${c}${d} in space, wanted ${String(ratio)}`;
  };
